import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useBranding, brandingFallback } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
    LayoutDashboard, Users, BookOpen, Palette, LogOut, Menu, X,
    GraduationCap, Award, Home, UserCircle2, CalendarCheck,
    Sparkles, Users2, Inbox, FileText, History,
} from "lucide-react";

const STUDENT_NAV = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/me/profile", label: "My Profile", icon: UserCircle2 },
    { to: "/me/attendance", label: "My Attendance", icon: CalendarCheck },
    { to: "/", label: "Browse Catalog", icon: BookOpen, end: true },
];

const INSTRUCTOR_NAV = [
    { to: "/instructor", label: "My Programs", icon: LayoutDashboard, end: true },
    { to: "/instructor/students", label: "My Students", icon: GraduationCap },
    { to: "/instructor/inbox", label: "Student Inbox", icon: Inbox },
    { to: "/me/profile", label: "My Profile", icon: UserCircle2 },
    { to: "/", label: "Public Catalog", icon: BookOpen, end: true },
];

const ADMIN_NAV = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/students", label: "Students", icon: GraduationCap },
    { to: "/admin/instructors", label: "Instructors", icon: Users2 },
    { to: "/admin/users", label: "All Users", icon: Users },
    { to: "/admin/programs", label: "Programs", icon: BookOpen },
    { to: "/admin/loa", label: "LOA Registry", icon: FileText },
    { to: "/admin/attendance", label: "Attendance", icon: CalendarCheck },
    { to: "/admin/delete-requests", label: "Delete Requests", icon: Inbox },
    { to: "/admin/audit-log", label: "Audit Log", icon: History },
    { to: "/admin/branding", label: "Branding", icon: Palette },
    { to: "/me/profile", label: "My Profile", icon: UserCircle2 },
    { to: "/", label: "Public Catalog", icon: Home, end: true },
];

const SME_NAV = [
    { to: "/sme", label: "Authoring Console", icon: LayoutDashboard, end: true },
    { to: "/me/profile", label: "My Profile", icon: UserCircle2 },
    { to: "/", label: "Public Catalog", icon: BookOpen, end: true },
];

const ROLE_META = {
    student: { label: "Student Portal", icon: GraduationCap, accent: "text-primary" },
    instructor: { label: "Instructor Portal", icon: Award, accent: "text-primary" },
    admin: { label: "Admin Console", icon: UserCircle2, accent: "text-primary" },
    super_admin: { label: "Super Admin Console", icon: UserCircle2, accent: "text-accent" },
    sme: { label: "SME Studio", icon: BookOpen, accent: "text-primary" },
};

function getNav(role) {
    if (role === "admin" || role === "super_admin") return ADMIN_NAV;
    if (role === "sme") return SME_NAV;
    if (role === "instructor") return INSTRUCTOR_NAV;
    return STUDENT_NAV;
}

function SidebarContent({ onNavigate }) {
    const { user, logout } = useAuth();
    const branding = useBranding();
    const navigate = useNavigate();
    const role = user?.role || "student";
    const meta = ROLE_META[role];
    const nav = getNav(role);

    // Pending delete-requests badge — only super admin can see/handle this.
    // Poll every 30 s so the dot disappears soon after a decision is made.
    const [pendingDeletes, setPendingDeletes] = useState(0);
    useEffect(() => {
        if (role !== "super_admin") return;
        let cancelled = false;
        const fetchCount = async () => {
            try {
                const { data } = await api.get("/admin/delete-requests/unread-count");
                if (!cancelled) setPendingDeletes(Number(data?.count) || 0);
            } catch {
                /* non-fatal */
            }
        };
        fetchCount();
        const iv = setInterval(fetchCount, 30000);
        return () => { cancelled = true; clearInterval(iv); };
    }, [role]);

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    return (
        <div className="relative flex flex-col h-full bg-card border-r border-border overflow-hidden">
            {/* Decorative watermark */}
            <Sparkles className="sidebar-watermark h-72 w-72 -left-16 -bottom-16 rotate-12" />

            {/* Brand */}
            <Link
                to="/"
                onClick={onNavigate}
                className="relative z-10 flex items-center gap-3 px-5 py-5 border-b border-border"
                data-testid="sidebar-logo-link"
            >
                <img
                    src={branding.logo_url}
                    alt=""
                    className="h-10 w-10 rounded-full object-contain bg-muted/60 p-1 ring-1 ring-border" onError={brandingFallback}
                />
                <div className="leading-tight">
                    <div className="font-display text-foreground text-lg tracking-tight">
                        Cambridge College
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-accent-foreground/70">
                        Online · PTIB Registered
                    </div>
                </div>
            </Link>

            {/* Role badge */}
            <div className="relative z-10 px-5 py-3 flex items-center gap-2 border-b border-border">
                <meta.icon className="h-3.5 w-3.5 text-primary" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                    {meta.label}
                </span>
            </div>

            {/* Nav */}
            <nav className="relative z-10 flex-1 overflow-y-auto px-3 py-4" data-testid="sidebar-nav">
                {nav.map((item) => {
                    const isDeleteReq = item.to === "/admin/delete-requests";
                    const showBadge = isDeleteReq && role === "super_admin" && pendingDeletes > 0;
                    return (
                        <NavLink
                            key={item.to + item.label}
                            to={item.to}
                            end={item.end}
                            onClick={onNavigate}
                            data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="nav-pill relative flex items-center gap-3 px-4 py-2.5 mb-0.5 rounded-lg text-sm text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                        >
                            <item.icon className="h-4 w-4" />
                            <span className="flex-1">{item.label}</span>
                            {showBadge && (
                                <span
                                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold leading-none animate-pulse"
                                    data-testid="sidebar-delete-requests-badge"
                                    title={`${pendingDeletes} pending delete ${pendingDeletes === 1 ? "request" : "requests"}`}
                                >
                                    {pendingDeletes > 99 ? "99+" : pendingDeletes}
                                </span>
                            )}
                        </NavLink>
                    );
                })}
            </nav>

            {/* User block */}
            <div className="relative z-10 border-t border-border p-4">
                <Link
                    to="/me/profile"
                    onClick={onNavigate}
                    className="flex items-center gap-3 mb-3 rounded-md hover:bg-muted/40 px-2 -mx-2 py-1 transition-colors"
                    data-testid="sidebar-user-link"
                >
                    <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-display ring-1 ring-primary/15">
                        {user?.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate text-foreground">{user?.name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                            {user?.student_id || user?.email}
                        </div>
                    </div>
                </Link>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLogout}
                    className="w-full bg-transparent border-border text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                    data-testid="sidebar-logout-btn"
                >
                    <LogOut className="h-4 w-4 mr-2" />
                    Log out
                </Button>
            </div>
        </div>
    );
}

export default function AppShell({ children }) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const { user } = useAuth();
    const branding = useBranding();

    if (!user) {
        // Unauthenticated — render children as-is (ProtectedRoute will redirect)
        return <>{children}</>;
    }

    const useBackOffice = user.role === "admin" || user.role === "super_admin" || user.role === "instructor" || user.role === "sme";

    return (
        <div
            className="min-h-screen flex bg-background"
            data-testid="app-shell"
            data-shell={useBackOffice ? "admin" : undefined}
        >
            {/* Desktop sidebar */}
            <aside className="hidden md:flex md:flex-col w-64 flex-shrink-0 h-screen sticky top-0">
                <SidebarContent />
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border h-14 flex items-center justify-between px-4">
                <div className="flex items-center gap-2">
                    <img
                        src={branding.logo_url}
                        alt=""
                        className="h-8 w-8 rounded-full object-contain bg-muted/40 p-0.5 ring-1 ring-border" onError={brandingFallback}
                    />
                    <div className="font-display text-base text-foreground">Cambridge College Online College Online</div>
                </div>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setMobileOpen(true)}
                    className="text-foreground hover:bg-muted/60"
                    data-testid="mobile-menu-open"
                >
                    <Menu className="h-5 w-5" />
                </Button>
            </div>

            {/* Mobile drawer */}
            {mobileOpen && (
                <div className="md:hidden fixed inset-0 z-[60]">
                    <div
                        className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
                        onClick={() => setMobileOpen(false)}
                        data-testid="mobile-menu-backdrop"
                    />
                    <div className="absolute left-0 top-0 h-full w-72 shadow-xl">
                        <div className="relative h-full">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setMobileOpen(false)}
                                className="absolute right-2 top-2 text-foreground hover:bg-muted/60 z-10"
                                data-testid="mobile-menu-close"
                            >
                                <X className="h-5 w-5" />
                            </Button>
                            <SidebarContent onNavigate={() => setMobileOpen(false)} />
                        </div>
                    </div>
                </div>
            )}

            {/* Main content */}
            <main className="flex-1 min-w-0 pt-14 md:pt-0" data-testid="app-shell-main">
                {children}
            </main>
        </div>
    );
}
